import { getFirestore, collection, query, orderBy, limit, startAfter, getDocs, doc, updateDoc, deleteDoc, increment, arrayUnion, arrayRemove, serverTimestamp, addDoc, onSnapshot, where, writeBatch, runTransaction, getDoc, setDoc } from "firebase/firestore";
import { app } from "../firebase-config.js";
import { InAppNotificationService } from "./in-app-notification.service.js";
import { DistributedCounterService } from "./common/distributed-counter.service.js"; // [NEW]

const db = getFirestore(app);

/**
 * PlazaService
 * 
 * Centralized service for Ssulwha Club (Plaza) operations.
 * Handles Posts, Comments, Likes, and Real-time subscriptions.
 * Adheres to Singleton pattern.
 */
class PlazaServiceImpl {
    constructor() {
        this.PAGE_SIZE = 20; // Improved from 50 to 20 for faster initial load
    }

    // --- Feeds (Read) ---

    /**
     * Subscribe to Feeds with optional Tag filter.
     * Note: For infinite scroll, real-time sync is complex.
     * We often use snapshot listener for the "Head" (Top items) or a simple query list.
     * For high-traffic, a hybrid approach is best: Listener for new, manual load for old.
     * 
     * Current Phase: Simpler Listener (as per original requirements) but optimized.
     */
    subscribeFeeds(tag, limitCount = 50, callback) {
        let q;
        if (tag) {
            // [OPTIMIZATION] Uses Composite Index: hashtags (Arrays) + createdAt (Desc)
            // Supported by Phase 1 Indexing.
            q = query(
                collection(db, 'plaza_feeds'),
                where('hashtags', 'array-contains-any', [`#${tag}`, tag]),
                orderBy('createdAt', 'desc'),
                limit(limitCount)
            );
        } else {
            q = query(collection(db, 'plaza_feeds'), orderBy('createdAt', 'desc'), limit(limitCount));
        }

        return onSnapshot(q, (snapshot) => {
            const feeds = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            // Client-side sort removed. Server delivers sorted data.
            callback(feeds);
        }, (error) => {
            console.error("[PlazaService] Feed Subscription Error (Check Indexes):", error);
            callback([], error);
        });
    }

    /**
     * Fetch Feeds (Pagination Support)
     * Returns { feeds, lastDoc }
     */
    async fetchFeeds({ tag = null, authorId = null, lastDoc = null, limitCount = 20 } = {}) {
        let constraints = [limit(limitCount)];

        if (tag) {
            // [OPTIMIZATION] Uses Composite Index
            constraints.push(where('hashtags', 'array-contains-any', [`#${tag}`, tag]));
            constraints.push(orderBy('createdAt', 'desc'));
        } else if (authorId) {
            constraints.push(where('authorId', '==', authorId));
            constraints.push(orderBy('createdAt', 'desc'));
        } else {
            constraints.push(orderBy('createdAt', 'desc'));
        }

        if (lastDoc) {
            constraints.push(startAfter(lastDoc));
        }

        const q = query(collection(db, 'plaza_feeds'), ...constraints);
        const snapshot = await getDocs(q);

        const feeds = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

        // Client-side sort removed.

        return {
            feeds,
            lastDoc: snapshot.docs[snapshot.docs.length - 1]
        };
    }

    async getFeed(feedId, options = {}) {
        try {
            const docRef = doc(db, 'plaza_feeds', feedId);
            let snapshot;

            if (options.source === 'server') {
                const { getDocFromServer } = await import("firebase/firestore");
                snapshot = await getDocFromServer(docRef);
            } else {
                snapshot = await getDoc(docRef);
            }

            if (!snapshot.exists()) return null;
            return { id: snapshot.id, ...snapshot.data() };
        } catch (e) {
            console.error("GetFeed Error", e);
            return null;
        }
    }


    // --- Mentions & Notifications ---

    /**
     * Search users for @mention
     * @param {string} term 
     */
    async searchUsers(term) {
        if (!term) return [];
        term = term.normalize('NFC');

        try {
            // [SECURE] Cloud Search Proxy
            // Direct query to 'users' collection is blocked by rules.
            const { getFunctions, httpsCallable } = await import("firebase/functions");
            const functions = getFunctions(app);
            const searchUsersFn = httpsCallable(functions, 'searchUsers');

            const result = await searchUsersFn({ query: term });
            return result.data || [];

        } catch (e) {
            console.error("User Search Error (Cloud)", e);
            return [];
        }
    }

    async sendNotification(targetUserId, { type, message, link, fromUserId, fromUserName, fromUserPhoto }) {
        await InAppNotificationService.createNotification(
            targetUserId,
            type,
            message,
            { uid: fromUserId, displayName: fromUserName, photoURL: fromUserPhoto },
            link
        );
    }

    // --- Actions (Write) ---

    async createPost(postData) {
        // [VALIDATION] at service level
        if (!postData.authorId) throw new Error("User ID required");
        if (!postData.content && (!postData.imageUrls || postData.imageUrls.length === 0)) {
            throw new Error("Content or Image required");
        }

        const payload = {
            ...postData,
            likes: 0,
            commentCount: postData.commentCount || 0, // [FIX] Allow pre-set count (for Threads)
            views: 0,
            createdAt: serverTimestamp(),
            // Ensure array fields exist
            hashtags: postData.hashtags || [],
            imageUrls: postData.imageUrls || [],
            likedBy: [],
            mentions: postData.mentions || [] // [NEW] Store mentioned UIDs
        };

        const docRef = await addDoc(collection(db, 'plaza_feeds'), payload);

        // [FIX] Update User Post Count (Skip for 'system')
        if (payload.authorId !== 'system') {
            const userRef = doc(db, 'users', payload.authorId);
            updateDoc(userRef, { postCount: increment(1) }).catch(e => console.warn("Post Count Update Error", e));
        }

        // [FIX] Quote Logic: Increment Repost Count & Notify
        if (payload.quoteData && payload.quoteData.id) {
            const originalFeedRef = doc(db, 'plaza_feeds', payload.quoteData.id);
            updateDoc(originalFeedRef, { repostCount: increment(1) }).catch(console.error);

            // Notify Original Author
            if (payload.quoteData.authorId && payload.quoteData.authorId !== payload.authorId) {
                this.sendNotification(payload.quoteData.authorId, {
                    type: 'mention', // or 'quote'? Let's use 'mention' type for now as 'quote' might not be handled in UI
                    message: `${payload.authorName}님이 회원님의 글을 인용했습니다.`,
                    link: `/plaza?id=${docRef.id}`,
                    fromUserId: payload.authorId,
                    fromUserName: payload.authorName,
                    fromUserPhoto: payload.authorPhoto
                });
            }
        }

        // Send Notifications to Mentioned Users
        if (payload.mentions && payload.mentions.length > 0) {
            payload.mentions.forEach(uid => {
                if (uid === payload.authorId) return; // Don't notify self
                // Avoid double notification if mentioned user is also the quoted author
                if (payload.quoteData && payload.quoteData.authorId === uid) return;

                this.sendNotification(uid, {
                    type: 'mention',
                    message: `${payload.authorName}님이 썰화광장에서 회원님을 언급했습니다.`,
                    link: `/plaza?id=${docRef.id}`, // [FIX] Deep Link
                    fromUserId: payload.authorId,
                    fromUserName: payload.authorName,
                    fromUserPhoto: payload.authorPhoto
                });
            });
        }

        return docRef;
    }

    async deletePost(feedId) {
        try {
            await deleteDoc(doc(db, 'plaza_feeds', feedId));
        } catch (e) {
            console.error("Delete Post Error", e);
            throw e; // Propagate to caller for UI handling
        }
    }

    async createThread(postsArray) {
        if (!postsArray || postsArray.length === 0) return;

        // [MODIFIED] Check if this is a thread (multiple parts)
        const isThread = postsArray.length > 1;

        // 1. Create Main Post (Inject hasThread flag)
        const mainData = postsArray[0];

        // [FIX] Robust Hashtag Parsing (for external calls)
        if (!mainData.hashtags || mainData.hashtags.length === 0) {
            mainData.hashtags = (mainData.content.match(/#[\w가-힣]+/g) || []);
        }

        const payload = {
            ...mainData,
            hasThread: isThread,
            threadCount: isThread ? postsArray.length - 1 : 0,
            commentCount: isThread ? postsArray.length - 1 : 0 // [FIX] Pre-set Comment Count for Atomicity
        };
        const mainRef = await this.createPost(payload);

        // 2. Add subsequent items as comments
        // Note: Sequential to ensure order
        for (let i = 1; i < postsArray.length; i++) {
            await this.addComment(mainRef.id, postsArray[i], { skipIncrement: true });
        }

        return mainRef;
    }

    /**
     * Repost a feed
     * @param {string} userId - Reposter ID
     * @param {object} originalFeed - Full feed object to snapshot
     */
    async repostFeed(currentUser, originalFeed) {
        // Prevent nested reposts (unwrap to original)
        let targetFeed = originalFeed;
        if (originalFeed.type === 'repost' && originalFeed.repostData) {
            targetFeed = originalFeed.repostData;
        }

        // Create Snapshot
        const snapshot = {
            id: targetFeed.id,
            authorId: targetFeed.authorId,
            authorName: targetFeed.authorName,
            authorPhoto: targetFeed.authorPhoto || null,
            content: targetFeed.content || '',
            imageUrls: targetFeed.imageUrls || [],
            createdAt: targetFeed.createdAt
        };

        const newFeedData = {
            authorId: currentUser.uid,
            authorName: currentUser.displayName || 'Unknown',
            authorPhoto: currentUser.photoURL || null,
            repostData: snapshot, // The snapshot of original
            type: 'repost',
            repostCount: 0,
            likeCount: 0,
            commentCount: 0, // [FIX] Initialize for consistency
            views: 0,        // [FIX] Initialize for consistency
            likes: [],
            hashtags: targetFeed.hashtags || [],
            createdAt: serverTimestamp()
        };

        try {
            await runTransaction(db, async (transaction) => {
                // 1. Create New Feed
                const newFeedRef = doc(collection(db, 'plaza_feeds'));
                transaction.set(newFeedRef, newFeedData);

                // 2. Update Original Feed Repost Count
                const originRef = doc(db, 'plaza_feeds', targetFeed.id);
                transaction.update(originRef, {
                    repostCount: increment(1)
                });

                // 3. Update User Post Count
                const userRef = doc(db, 'users', currentUser.uid);
                transaction.update(userRef, {
                    postCount: increment(1)
                });
            });
            return true;
        } catch (error) {
            console.error("Repost Error", error);
            throw error;
        }
    }

    // --- Interaction ---

    /**
     * [Distributed] Toggle Like
     * Uses subcollection 'likes/{uid}' to check state (Unlimited scale).
     * Uses sharded counter for 'likes' count (Unlimited throughput).
     */
    async toggleLike(feedId, userId, isCurrentlyLiked) {
        const feedRef = doc(db, 'plaza_feeds', feedId);
        const userLikeRef = doc(db, 'plaza_feeds', feedId, 'likes', userId);

        // Dynamic Import to avoid cycle (or just standard import if at top)
        // Let's assume standard import for now, or use the helper directly if we import it.
        // Ideally we import at top. I will use the imported Service.

        // Checking "isCurrentlyLiked" is optimistic from UI. 
        // For robustness, we should check doc existence, but for speed, we trust param or UI.
        // Actually, to be safe in Distributed system, we check subcollection.

        const userLikeSnap = await getDoc(userLikeRef);
        const exists = userLikeSnap.exists();

        if (exists) {
            // Unlike
            await deleteDoc(userLikeRef);
            await DistributedCounterService.decrement(feedRef, 'shards');

            // [HYBRID] Also decrement main doc for immediate UI compatibility (till we fully migrate to reading shards)
            // Note: This reintroduced the bottleneck slightly, but allows sorting to work without Aggregator.
            // For true high-scale, we should use Cloud Function to aggregate periodically.
            // But user asked for "Distributed Counter".
            await updateDoc(feedRef, { likes: increment(-1), likedBy: arrayRemove(userId) }).catch(() => { });
        } else {
            // Like
            await setDoc(userLikeRef, { createdAt: serverTimestamp() });
            await DistributedCounterService.increment(feedRef, 'shards');

            // [HYBRID]
            await updateDoc(feedRef, { likes: increment(1), likedBy: arrayUnion(userId) }).catch(() => { });
        }
    }

    async incrementView(feedId) {
        if (!feedId) return;
        try {
            // [SECURE] Use Cloud Function for reliable counting (Guests + Missing Fields support)
            const { getFunctions, httpsCallable } = await import("firebase/functions");
            const functions = getFunctions(app);
            const logFeedViewFn = httpsCallable(functions, 'logFeedView');

            // Fire and forget
            logFeedViewFn({ feedId }).catch(e => console.warn("LogFeedView Server Error", e));
        } catch (e) {
            console.error("PlazaService.incrementView Client Error", e);
        }
    }

    // --- Comments ---

    subscribeComments(feedId, callback) {
        const q = query(collection(db, 'plaza_feeds', feedId, 'comments'), orderBy('createdAt', 'asc'));
        return onSnapshot(q, (snapshot) => {
            const comments = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            callback(comments);
        });
    }

    /**
     * Fetch Comments (Pagination Support)
     * [NEW] For Scalability, replacing Real-time listener.
     */
    async getComments(feedId, limitCount = 20, lastDoc = null, options = {}) {
        let constraints = [
            orderBy('createdAt', 'asc'), // Chronological order for comments
            limit(limitCount)
        ];

        if (lastDoc) {
            constraints.push(startAfter(lastDoc));
        }

        const q = query(collection(db, 'plaza_feeds', feedId, 'comments'), ...constraints);
        const snapshot = await getDocs(q);

        const comments = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

        return {
            comments,
            lastDoc: snapshot.docs[snapshot.docs.length - 1]
        };
    }

    // [COMPATIBILITY] Alias for cached clients calling old method name
    async fetchComments(...args) {
        console.warn("Deprecated: fetchComments called. Use getComments.");
        return this.getComments(...args);
    }

    async addComment(feedId, commentData, options = {}) { // [MODIFIED] Added options
        const payload = {
            ...commentData,
            createdAt: serverTimestamp(),
            mentions: commentData.mentions || [] // [NEW]
        };

        const batch = writeBatch(db);

        // 1. Create Comment
        const commentRef = doc(collection(db, 'plaza_feeds', feedId, 'comments'));
        batch.set(commentRef, payload);

        // 2. Increment Counter (Unless skipped for Thread Atomicity)
        if (!options.skipIncrement) {
            const feedRef = doc(db, 'plaza_feeds', feedId);
            batch.update(feedRef, {
                commentCount: increment(1)
            });
        }

        await batch.commit();

        // Notifications
        // 1. Notify Feed Owner (if not self)
        // Need to fetch feed owner? Calling client should perhaps handle or we do a read here?
        // Optimized: Client passes feedOwnerId? Or we just notify Mentions for now.

        if (payload.mentions && payload.mentions.length > 0) {
            payload.mentions.forEach(uid => {
                if (uid === payload.authorId) return;
                this.sendNotification(uid, {
                    type: 'mention',
                    message: `${payload.authorName}님이 댓글에서 회원님을 언급했습니다.`,
                    link: `/plaza?id=${feedId}&commentId=${commentRef.id}`, // [FIX] Deep Link + Target Lock
                    fromUserId: payload.authorId,
                    fromUserName: payload.authorName,
                    fromUserPhoto: payload.authorPhoto
                });
            });
        }
    }

    async deleteComment(feedId, commentId) {
        await deleteDoc(doc(db, 'plaza_feeds', feedId, 'comments', commentId));
        await updateDoc(doc(db, 'plaza_feeds', feedId), {
            commentCount: increment(-1)
        });
    }

    // --- Admin ---
    // Moved to src/services/admin/admin-plaza.service.js
    // to reduce main bundle size.

}

export const PlazaService = new PlazaServiceImpl();
